/**
 * Transcript scroll policy — pure math and tiny data structures for the
 * pinned-to-bottom scroll model shared by every chat surface (GlobalChatSystem,
 * the landing-page demo chat, the Hidalgo widget, Moonshot voice chat).
 *
 * No DOM access in this module: everything operates on plain numbers and ids
 * so it can be unit-tested without jsdom geometry stubs, mirroring
 * components/GlobalChatSystem/threadNav.ts. The stateful controller that owns
 * the DOM lives in src/hooks/useTranscriptScroll.ts.
 */

/**
 * Distance-from-bottom band (px) inside which the viewport counts as "at the
 * bottom" and stays pinned. Deliberately small: the old 600px threshold
 * treated "scrolled up half a screen, reading" as at-bottom and yanked the
 * user down on every content update.
 */
export const BOTTOM_PIN_BAND_PX = 120;

/** Distance from the top (px) that counts as "near top" (shows the previous-X pill). */
export const NEAR_TOP_THRESHOLD_PX = 150;

/** How long a saved per-thread reading position stays restorable. */
export const THREAD_SCROLL_MEMORY_TTL_MS = 30 * 60 * 1000;

/** Max remembered threads before the oldest entry is evicted. */
export const THREAD_SCROLL_MEMORY_CAP = 50;

/**
 * Window after a thread switch during which unpinned append/edit changes are
 * absorbed silently (server hydration lands right after a switch and must not
 * light a false "N new" badge).
 */
export const POST_SWITCH_SETTLE_MS = 1500;

/** Downward finger travel (px) that counts as an intentional scroll-up on touch. */
export const TOUCH_UNPIN_SLOP_PX = 8;

/**
 * How far from the bottom a reader can be and still be CARRIED DOWN by a new
 * message at the tail (2026-09-13).
 *
 * Distinct from BOTTOM_PIN_BAND_PX above, and deliberately wider. That one
 * answers "is the viewport pinned?" and has to stay tight — at 600px it used to
 * treat "scrolled up half a screen, reading" as at-bottom. This one answers a
 * narrower question: a message just ARRIVED at the tail, and the reader is
 * sitting near it — do we follow?
 *
 * Owner report: "if the user is viewing a message that was just sent and
 * they're towards the bottom, and a new message comes in from another user,
 * then it needs to bring the thread down to the bottom." The pin band alone
 * does not deliver that, because the pin is lost by ordinary drift — the
 * keyboard opening, the composer growing a line, a few pixels of thumb — and
 * once lost, nothing brings you back until you tap.
 *
 * 320px is about two message rows: close enough that the new message is part of
 * what you are already reading, far enough that someone who scrolled up to
 * re-read something is left alone. It applies ONLY to genuine tail appends,
 * never to edits, so a streaming reply cannot drag a reader around.
 */
export const ARRIVAL_FOLLOW_BAND_PX = 320;

/** Programmatic smooth scrolls count as arrived within this many px of target. */
export const PROGRAMMATIC_ARRIVAL_EPSILON_PX = 4;

/** Safety timeout for programmatic smooth-scroll tracking (interrupted animations). */
export const PROGRAMMATIC_SCROLL_TIMEOUT_MS = 700;

export function distanceFromBottom(
  scrollTop: number,
  scrollHeight: number,
  clientHeight: number,
): number {
  return Math.max(0, scrollHeight - scrollTop - clientHeight);
}

export function isWithinBottomBand(
  scrollTop: number,
  scrollHeight: number,
  clientHeight: number,
  bandPx: number = BOTTOM_PIN_BAND_PX,
): boolean {
  return distanceFromBottom(scrollTop, scrollHeight, clientHeight) <= bandPx;
}

export function isNearTranscriptTop(
  scrollTop: number,
  thresholdPx: number = NEAR_TOP_THRESHOLD_PX,
): boolean {
  return scrollTop < thresholdPx;
}

// ────────────────────────────────────────────────────────────────────────────
// Items-change classification
// ────────────────────────────────────────────────────────────────────────────

/** Cheap fingerprint of a transcript's item list. */
export interface TranscriptItemsShape {
  firstId: string | null;
  lastId: string | null;
  count: number;
}

export function shapeOfItems(
  items: ReadonlyArray<{ id?: string | null }>,
): TranscriptItemsShape {
  return {
    firstId: items.length ? (items[0].id ?? null) : null,
    lastId: items.length ? (items[items.length - 1].id ?? null) : null,
    count: items.length,
  };
}

/**
 * - `append`  — items grew at the tail (new message). The only kind that may
 *               increment the "N new" badge while unpinned.
 * - `prepend` — items grew at the head with the tail unchanged (load-earlier
 *               page). The surface's own anchor effect owns the scroll
 *               correction; the controller must stay inert and never badge.
 * - `edit`    — same ends, same count: in-place content mutation (streaming
 *               update, attachment patch).
 * - `replace` — anything else (clear chat, wholesale rebuild).
 * - `none`    — identical shape references aside, nothing measurable changed.
 */
export type TranscriptChangeKind = 'none' | 'append' | 'prepend' | 'edit' | 'replace';

export function classifyItemsChange(
  prev: TranscriptItemsShape,
  next: TranscriptItemsShape,
): TranscriptChangeKind {
  if (
    prev.count === next.count
    && prev.firstId === next.firstId
    && prev.lastId === next.lastId
  ) {
    // The caller only classifies when the items identity changed, so an
    // unchanged shape means an in-place mutation of some item's content.
    return prev.count === 0 ? 'none' : 'edit';
  }
  if (next.count > prev.count) {
    if (prev.count === 0) return 'append';
    if (prev.firstId === next.firstId && prev.lastId !== next.lastId) return 'append';
    if (prev.lastId === next.lastId && prev.firstId !== next.firstId) return 'prepend';
    return 'replace';
  }
  return 'replace';
}

// ────────────────────────────────────────────────────────────────────────────
// Arrival policy — what a new message at the tail does to the viewport
// ────────────────────────────────────────────────────────────────────────────

/**
 * WHO the arriving item came from. `classifyItemsChange` above can only see
 * that the list grew; it cannot tell a teammate's message from Prest0n's
 * streaming tokens from the echo of your own send in another tab. Those three
 * deserve different treatment and used to get the same.
 */
export type TranscriptArrivalKind = 'self' | 'peer' | 'assistant' | 'system';

/**
 * What the controller should do about a change to the transcript.
 *
 * - `stick-instant`    — already pinned; keep the bottom edge glued, no animation.
 * - `follow-to-bottom` — not pinned, but close enough and this is a real
 *                        arrival: glide down and re-pin.
 * - `badge-count`      — too far away; count it on the pill instead of moving.
 * - `badge-activity`   — something changed that is not a countable arrival.
 * - `ignore`           — move nothing, light nothing.
 */
export type ArrivalAction =
  | 'stick-instant'
  | 'follow-to-bottom'
  | 'badge-count'
  | 'badge-activity'
  | 'ignore';

/**
 * The one place the arrival question is answered.
 *
 * Pure so the whole matrix can be tested at every distance and every kind,
 * rather than eyeballed against a running app — which is the only reason the
 * "never yank a reader who scrolled up" property below is checkable at all.
 */
export function decideArrivalAction(args: {
  pinned: boolean;
  /**
   * Distance from the bottom BEFORE the new content was laid out. The
   * post-layout number is inflated by the very message being judged, which
   * would make every arrival look far away.
   */
  distanceFromBottomPx: number;
  changeKind: TranscriptChangeKind;
  arrivalKind: TranscriptArrivalKind;
  /** Inside the post-thread-switch settle window (server hydration landing). */
  withinSettle: boolean;
}): ArrivalAction {
  const {
    pinned, distanceFromBottomPx, changeKind, arrivalKind, withinSettle,
  } = args;

  if (changeKind === 'none') return 'ignore';
  // Load-earlier: the surface's own anchor effect owns the correction, and a
  // badge for content the reader deliberately fetched would be nonsense.
  if (changeKind === 'prepend') return 'ignore';
  // Pinned wins over everything: this is the existing behaviour and the reason
  // most arrivals never reach the rest of this function.
  if (pinned) return 'stick-instant';
  // Hydration right after a thread switch is not an arrival.
  if (withinSettle) return 'ignore';

  if (changeKind === 'append') {
    // Your own message, sent from anywhere: always follow. This mirrors the
    // send path's own unconditional re-pin — the one auto-scroll allowed to
    // override an unpinned reader — and covers the echo arriving from another
    // tab, which that path never sees.
    if (arrivalKind === 'self') return 'follow-to-bottom';
    if (
      (arrivalKind === 'peer' || arrivalKind === 'assistant')
      && distanceFromBottomPx <= ARRIVAL_FOLLOW_BAND_PX
    ) {
      return 'follow-to-bottom';
    }
    return 'badge-count';
  }

  // An EDIT is not an arrival. Streaming tokens are edits, and following them
  // would drag a reader down one frame at a time — the failure mode that makes
  // the whole pin model necessary. A peer's edit still lights the pill so the
  // thread does not change silently underneath someone.
  if (changeKind === 'edit') {
    return arrivalKind === 'peer' ? 'badge-activity' : 'ignore';
  }

  // 'replace' — a wholesale rebuild. Something changed, nothing is countable.
  return 'badge-activity';
}

/**
 * Did a downward finger drag actually scroll anything?
 *
 * `TOUCH_UNPIN_SLOP_PX` of travel alone was the old test, and it unpins even
 * when the scroller is ALREADY at the bottom and cannot move: an iOS
 * rubber-band bounce, a thumb resting on the transcript, a tap that drifts.
 * The pin is then silently gone, and the next message does not follow — which
 * is one of the ways "a new message came in and nothing happened" happens on a
 * phone.
 *
 * Requiring `scrollTop` to have actually decreased makes the signal mean what
 * it says: the content moved with the finger. An overscroll bounce leaves
 * `scrollTop` pinned at its maximum and no longer counts.
 *
 * NOTE: this is a hypothesis about iOS that cannot be verified in a Chromium
 * harness — it emulates the viewport, not the engine, and does not rubber-band.
 * It is the secondary fix for that reason; the follow band above is what makes
 * the reported behaviour work whether or not this diagnosis is right.
 */
export function shouldUnpinFromTouchDrag(args: {
  fingerTravelPx: number;
  scrollTopAtStart: number;
  scrollTopNow: number;
  slopPx?: number;
}): boolean {
  const slop = args.slopPx ?? TOUCH_UNPIN_SLOP_PX;
  if (!(args.fingerTravelPx > slop)) return false;
  return args.scrollTopNow < args.scrollTopAtStart;
}

// ────────────────────────────────────────────────────────────────────────────
// History prefetch — scrolling up loads older messages, with no button
// ────────────────────────────────────────────────────────────────────────────

/**
 * How much runway the reader gets before the top, as a multiple of the
 * viewport (2026-09-14).
 *
 * VIEWPORT-RELATIVE IS THE WHOLE DESKTOP/MOBILE ADAPTATION. No user-agent
 * sniffing, no `isMobileViewport` read, and it tracks continuously while a
 * window is dragged between the two. A fixed pixel lead is wrong at both
 * ends: 150px is a large slice of a 380px phone transcript (firing almost
 * immediately, for nothing) and a sliver of an 1100px desktop pane, where one
 * wheel notch is ~100px and the reader would reach the hard top well before a
 * round trip returns. "About a screen of runway" reads the same on both.
 */
export const HISTORY_PREFETCH_VIEWPORT_FACTOR = 1.25;

/** Floor for the prefetch zone, for very short panes. */
export const HISTORY_PREFETCH_MIN_PX = 200;

/** Ceiling, so a tall desktop pane does not fetch a screen and a half early. */
export const HISTORY_PREFETCH_MAX_PX = 1600;

/**
 * Leaving the zone re-arms the trigger only past this multiple of it —
 * hysteresis, so a reader hovering on the boundary cannot chatter.
 */
export const HISTORY_PREFETCH_REARM_FACTOR = 2;

/**
 * Minimum gap between two automatic fetches.
 *
 * `isLoadingEarlierMessages` lags the request by a commit, so without this a
 * slow connection can spend several reads before the first one is visible in
 * state. Load-bearing for the reload case in particular: paging cursors are
 * opaque Firestore snapshots that do not survive a reload, and the first call
 * afterwards re-establishes one with an extra read.
 */
export const HISTORY_AUTO_LOAD_COOLDOWN_MS = 400;

/**
 * Pages fetched per APPROACH — one arrival at the top, however many pages it
 * takes before the reader leaves the zone again.
 */
export const HISTORY_AUTO_LOAD_MAX_PER_APPROACH = 4;

/** Pages fetched per thread per session, before the manual control takes over. */
export const HISTORY_AUTO_LOAD_MAX_PAGES_PER_THREAD = 10;

/** Consecutive failures before automatic loading stands down for the thread. */
export const HISTORY_AUTO_LOAD_MAX_FAILURES = 2;

/** Backoff ceiling, so a persistent failure does not retry forever on a timer. */
export const HISTORY_AUTO_LOAD_BACKOFF_CEILING_MS = 8_000;

/**
 * The distance from the top at which older history starts loading.
 *
 * Two clamps are load-bearing, not tidiness. `clientHeight` is 0 while the
 * pane is hidden or pre-layout, which would otherwise read as "at the top".
 * And the zone never takes more than half the scrollable range: a transcript
 * only slightly taller than its viewport would otherwise be inside the zone
 * the instant it opens at the bottom, and every thread open would spend a
 * server read.
 */
export function historyPrefetchDistancePx(
  clientHeight: number,
  scrollHeight: number,
): number {
  if (!(clientHeight > 0)) return 0;
  const viewportRelative = clientHeight * HISTORY_PREFETCH_VIEWPORT_FACTOR;
  const clamped = Math.min(
    Math.max(viewportRelative, HISTORY_PREFETCH_MIN_PX),
    HISTORY_PREFETCH_MAX_PX,
  );
  const scrollable = Math.max(0, scrollHeight - clientHeight);
  return Math.min(clamped, scrollable / 2);
}

/**
 * - `fetch`  — the reader has arrived in the zone and the trigger is armed.
 * - `rearm`  — they have walked back down past the hysteresis line; the next
 *              approach is a fresh one.
 * - `idle`   — nothing to do.
 */
export type HistoryPrefetchSignal = 'idle' | 'fetch' | 'rearm';

/**
 * Should the surface be asked for older history right now?
 *
 * `pinned` is the honest encoding of "the user scrolled up": it is already
 * flipped by wheel-up, a content-moving touch drag, ArrowUp/PageUp/Home, and
 * leaving the bottom band. Reading it here is what stops a thread that opens
 * at the bottom — inside the zone, because it is short — from fetching before
 * anybody has touched it.
 */
export function decideHistoryPrefetch(args: {
  scrollTop: number;
  clientHeight: number;
  scrollHeight: number;
  /** Pinned to the tail — the reader has not scrolled up. */
  pinned: boolean;
  armed: boolean;
  /** Inside the post-thread-switch settle window (hydration, not a gesture). */
  withinSettle: boolean;
}): HistoryPrefetchSignal {
  const { scrollTop, clientHeight, scrollHeight, pinned, armed, withinSettle } = args;
  const trigger = historyPrefetchDistancePx(clientHeight, scrollHeight);
  // Re-arm is checked FIRST and independently of pinned/settle: a reader who
  // walked back down has ended this approach whatever else is true.
  if (trigger > 0 && scrollTop > trigger * HISTORY_PREFETCH_REARM_FACTOR) {
    return armed ? 'idle' : 'rearm';
  }
  if (!armed) return 'idle';
  if (trigger <= 0) return 'idle';
  if (pinned) return 'idle';
  if (withinSettle) return 'idle';
  return scrollTop <= trigger ? 'fetch' : 'idle';
}

/** What the surface should actually do when the trigger fires. */
export type HistoryAutoLoadAction = 'reveal-local' | 'fetch-page' | 'skip';

export interface HistoryAutoLoadState {
  /** Pages fetched during the current approach. */
  spentThisApproach: number;
  /** Pages fetched for this thread this session. */
  spentThisThread: number;
  /** Consecutive failures (an error, or a fetch that landed nothing). */
  failures: number;
  /** No automatic fetch before this instant. */
  cooldownUntil: number;
  /** Automatic loading has stood down for this thread. */
  exhausted: boolean;
}

export function initialHistoryAutoLoadState(): HistoryAutoLoadState {
  return {
    spentThisApproach: 0,
    spentThisThread: 0,
    failures: 0,
    cooldownUntil: 0,
    exhausted: false,
  };
}

/**
 * One layer per trigger, local history first.
 *
 * The reader walks local history then server history in reading order — the
 * same sequence the manual control performs in a single click, spread across
 * two triggers so neither jumps the viewport.
 */
export function decideHistoryAutoLoad(args: {
  state: HistoryAutoLoadState;
  now: number;
  /** Local history is collapsed and holds rows (`!showAllMessages && hiddenCount > 0`). */
  hasCollapsedLocalHistory: boolean;
  /** The server holds pages older than the loaded window. */
  earlierAvailable: boolean;
  /** A fetch is already in flight. */
  isLoading: boolean;
  /**
   * The persisted-view restore owns paging right now. It runs its own bounded
   * three-page loop and deliberately leaves the prepend anchor null; an
   * automatic fetch that sets the anchor mid-restore makes the two fight over
   * scrollTop.
   */
  restoreOwnsPaging: boolean;
  /** Thread paging is available on this surface at all. */
  pagingEnabled: boolean;
}): HistoryAutoLoadAction {
  const {
    state, now, hasCollapsedLocalHistory, earlierAvailable,
    isLoading, restoreOwnsPaging, pagingEnabled,
  } = args;
  if (!pagingEnabled) return 'skip';
  if (restoreOwnsPaging) return 'skip';
  // Revealing local history costs nothing and touches no network, so it is
  // not subject to the fetch budget — but it does wait for an in-flight page,
  // or the two corrections would land in the same frame.
  if (hasCollapsedLocalHistory) return isLoading ? 'skip' : 'reveal-local';
  if (isLoading) return 'skip';
  if (state.exhausted) return 'skip';
  if (!earlierAvailable) return 'skip';
  if (now < state.cooldownUntil) return 'skip';
  if (state.spentThisApproach >= HISTORY_AUTO_LOAD_MAX_PER_APPROACH) return 'skip';
  if (state.spentThisThread >= HISTORY_AUTO_LOAD_MAX_PAGES_PER_THREAD) return 'skip';
  return 'fetch-page';
}

/**
 * - `dispatched`  — a fetch was just sent.
 * - `settled`     — it returned and history actually grew.
 * - `no-progress` — it returned and the transcript head did not move. Counts
 *                   as a failure: `loadEarlierMessages` has three early
 *                   returns that leave "older available" true while landing
 *                   nothing (an empty page, a page entirely of live-task
 *                   copies, a page whose rows the pane already holds), and in
 *                   each the cursor has advanced while nothing visible
 *                   changed. Without this an idle reader parked in the zone
 *                   walks the cursor to the end of the thread silently.
 * - `failed`      — it threw.
 * - `new-approach`— the reader left the zone and came back.
 * - `thread-switch` — a different thread; everything resets.
 */
export type HistoryAutoLoadOutcome =
  | 'dispatched'
  | 'settled'
  | 'no-progress'
  | 'failed'
  | 'new-approach'
  | 'thread-switch';

export function nextHistoryAutoLoadState(
  prev: HistoryAutoLoadState,
  outcome: HistoryAutoLoadOutcome,
  now: number,
): HistoryAutoLoadState {
  switch (outcome) {
    case 'dispatched':
      return {
        ...prev,
        spentThisApproach: prev.spentThisApproach + 1,
        spentThisThread: prev.spentThisThread + 1,
        cooldownUntil: now + HISTORY_AUTO_LOAD_COOLDOWN_MS,
      };
    case 'settled':
      return { ...prev, failures: 0 };
    case 'no-progress':
    case 'failed': {
      const failures = prev.failures + 1;
      const backoff = Math.min(
        HISTORY_AUTO_LOAD_COOLDOWN_MS * (2 ** failures),
        HISTORY_AUTO_LOAD_BACKOFF_CEILING_MS,
      );
      return {
        ...prev,
        failures,
        cooldownUntil: now + backoff,
        exhausted: prev.exhausted || failures >= HISTORY_AUTO_LOAD_MAX_FAILURES,
      };
    }
    case 'new-approach':
      return { ...prev, spentThisApproach: 0 };
    case 'thread-switch':
      return initialHistoryAutoLoadState();
    default:
      return prev;
  }
}

// ────────────────────────────────────────────────────────────────────────────
// Per-thread scroll memory ("pick up where you left off" within a session)
// ────────────────────────────────────────────────────────────────────────────

export interface ThreadScrollMemoryEntry {
  scrollTop: number;
  pinned: boolean;
  savedAt: number;
}

export type ThreadScrollMemory = Map<string, ThreadScrollMemoryEntry>;

export function recordThreadScroll(
  memory: ThreadScrollMemory,
  key: string,
  entry: ThreadScrollMemoryEntry,
  cap: number = THREAD_SCROLL_MEMORY_CAP,
): void {
  memory.set(key, entry);
  while (memory.size > cap) {
    let oldestKey: string | null = null;
    let oldestAt = Infinity;
    memory.forEach((value, mapKey) => {
      if (value.savedAt < oldestAt) {
        oldestAt = value.savedAt;
        oldestKey = mapKey;
      }
    });
    if (oldestKey === null) break;
    memory.delete(oldestKey);
  }
}

/**
 * Returns the remembered position for `key` if it is still fresh, dropping
 * (and returning null for) stale entries. In-memory only by design: a page
 * reload always starts at the bottom of the active thread — that IS the
 * "coming back" policy — and no cross-surface localStorage key exists to
 * clobber.
 */
export function lookupFreshThreadScroll(
  memory: ThreadScrollMemory,
  key: string,
  now: number,
  ttlMs: number = THREAD_SCROLL_MEMORY_TTL_MS,
): ThreadScrollMemoryEntry | null {
  const entry = memory.get(key);
  if (!entry) return null;
  if (now - entry.savedAt > ttlMs) {
    memory.delete(key);
    return null;
  }
  return entry;
}
